import React, { useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  CardActions,
  Grid
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import { EHRButton } from 'src/components/ui/v1';
import { ReactComponent as PortraitLogo } from 'src/assets/images/portrait-logo.svg';
import { ReactComponent as NewEhrIcon } from 'src/assets/images/new-ehr-sample.svg';
import oldEhrImage from 'src/assets/images/old_ehr_migration_sample_image.png';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    height: '100vh',
    justifyContent: 'center',
    paddingBottom: '2rem',
  },
  content: {
    display: 'flex',
    gap: '2rem',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '600px',
    [theme.breakpoints.down('xs')]: {
      minWidth: '90vw',
    },
  },
  logoContainer: {
    width: '300px',
    maxWidth: '75%',
    marginBottom: '2rem',
  },
  choicesContainer: {
    width: '100%',
  },
  choiceCard: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8],
    },
  },
  cardContent: {
    flexGrow: 1,
    textAlign: 'center',
    padding: `${theme.spacing(3)}px ${theme.spacing(3)}px 0 ${theme.spacing(3)}px`,
  },
  cardActions: {
    justifyContent: 'center',
    padding: 0,
    marginTop: `-${theme.spacing(2)}px`,
  },
  icon: {
    fontSize: '3rem',
    marginBottom: theme.spacing(2),
  },
  newEhrIcon: {
    width: '100%',
    height: '350px',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    position: 'relative',
    '& svg': {
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      width: '100%',
      height: 'auto',
      maxHeight: '100%',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '300px',
      background: 'linear-gradient(to bottom, transparent 0%, rgba(255, 255, 255, 0.3) 30%, ' +
        'rgba(255, 255, 255, 0.7) 60%, rgba(255, 255, 255, 1) 100%)',
      pointerEvents: 'none',
    },
    '& path[fill="#10B981"], & path[fill="#059669"], & path[fill="#047857"], & path[fill="green"]': {
      fill: '#F48643',
    },
    '& path[fill="#22C55E"], & path[fill="#16A34A"], & path[fill="#15803D"], & path[fill="#166534"]': {
      fill: '#F48643',
    },
    '& path[stroke="#10B981"], & path[stroke="#059669"], & path[stroke="#047857"], & path[stroke="green"]': {
      stroke: '#F48643',
    },
    '& path[stroke="#22C55E"], & path[stroke="#16A34A"], & path[stroke="#15803D"], & path[stroke="#166534"]': {
      stroke: '#F48643',
    },
    '& circle[fill*="green"], & rect[fill*="green"], & ellipse[fill*="green"]': {
      fill: '#F48643',
    },
    '& circle[stroke*="green"], & rect[stroke*="green"], & ellipse[stroke*="green"]': {
      stroke: '#F48643',
    },
  },
  title: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  newEhrTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: '#F48643',
    position: 'absolute',
    bottom: '80px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 3,
    textAlign: 'center',
    width: '100%',
  },
  orangeButton: {
    backgroundColor: '#F48643 !important',
    color: 'white !important',
    border: '1px solid #F48643 !important',
    '&:hover': {
      backgroundColor: '#E0753A !important',
    },
    '&:disabled': {
      backgroundColor: '#F48643 !important',
      opacity: 0.6,
    },
  },
  description: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  newEhrDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 3,
    textAlign: 'center',
    width: '90%',
    padding: '0 16px',
  },
  newEhrCard: {
    borderColor: '#F48643',
    borderWidth: '2px',
    overflow: 'hidden',
    position: 'relative',
  },
  viewOnlyCard: {
    borderColor: theme.palette.grey[400],
    borderWidth: '1px',
    position: 'relative',
    overflow: 'hidden',
  },
  oldEhrIcon: {
    width: '100%',
    height: '350px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    position: 'relative',
    backgroundImage: `url(${oldEhrImage})`,
    backgroundSize: 'contain',
    backgroundPosition: 'center top',
    backgroundRepeat: 'no-repeat',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '300px',
      background: 'linear-gradient(to bottom, transparent 0%, rgba(255, 255, 255, 0.3) 30%, ' +
        'rgba(255, 255, 255, 0.7) 60%, rgba(255, 255, 255, 1) 100%)',
      pointerEvents: 'none',
    },
  },
  oldEhrTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
    position: 'absolute',
    bottom: '80px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 3,
    textAlign: 'center',
    width: '100%',
  },
  oldEhrDescription: {
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
    position: 'absolute',
    bottom: '20px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 3,
    textAlign: 'center',
    width: '90%',
    padding: '0 16px',
  },
}));

const MigrationChoicePage: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<'new' | 'old' | null>(null);

  // Extract email from URL params
  const urlParams = new URLSearchParams(location.search);
  const email = urlParams.get('email');

  const handleNewEhrChoice = async () => {
    if (!email) {
      return;
    }
    
    setIsLoading('new');
    
    try {
      // Clear view-only mode flag since they're choosing the new system
      sessionStorage.removeItem('view_only_mode');
      
      const newEhrDomain = process.env.REACT_APP_NEW_EHR_DOMAIN || 'http://localhost:3000';
      const newEhrUrl = `${newEhrDomain}/login?email=${encodeURIComponent(email)}`;
      window.location.href = newEhrUrl;
    } catch (error) {
      setIsLoading(null);
    }
  };

  const handleOldEhrChoice = () => {
    if (!email) {
      return;
    }
    
    setIsLoading('old');
    
    // Set view-only mode flag in session storage
    sessionStorage.setItem('view_only_mode', 'true');
    
    // Redirect to standard login with email pre-populated
    history.push(`/login?email=${encodeURIComponent(email)}`);
  };

  if (!email) {
    return (
      <Container component="main" className={classes.container}>
        <Alert severity="error">
          Invalid request. Please start from the login page.
        </Alert>
      </Container>
    );
  }

  return (
    <Container component="main" className={classes.container}>
      <Box className={classes.content}>
        <div className={classes.logoContainer}>
          <PortraitLogo />
        </div>
        
        <Typography variant="h4" align="center" gutterBottom>
          Welcome Back!
        </Typography>
        
        <Typography variant="body1" align="center" color="textSecondary" style={{ marginBottom: '2rem' }}>
          Your account has been migrated to our new system. Please choose how you&rsquo;d like to proceed:
        </Typography>

        <Grid container spacing={3} className={classes.choicesContainer}>
          <Grid item xs={12} md={6}>
            <Card 
              className={`${classes.choiceCard} ${classes.newEhrCard}`}
              variant="outlined"
              onClick={handleNewEhrChoice}
            >
              <CardContent className={classes.cardContent}>
                <Box className={classes.newEhrIcon}>
                  <NewEhrIcon />
                  <Typography variant="h5" className={classes.newEhrTitle}>
                    New System
                  </Typography>
                  <Typography variant="body2" className={classes.newEhrDescription}>
                    Access the new and improved Portrait system with enhanced features,
                    better performance, and modern interface.
                  </Typography>
                </Box>
              </CardContent>
              <CardActions className={classes.cardActions}>
                <EHRButton
                  variant="contained"
                  color={undefined}
                  className={classes.orangeButton}
                  text={isLoading === 'new' ? 'Redirecting...' : 'Continue to New System'}
                  disabled={!!isLoading}
                  fullWidth
                />
              </CardActions>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card 
              className={`${classes.choiceCard} ${classes.viewOnlyCard}`}
              variant="outlined"
              onClick={handleOldEhrChoice}
            >
              <CardContent className={classes.cardContent}>
                <Box className={classes.oldEhrIcon}>
                  <Typography variant="h5" className={classes.oldEhrTitle}>
                    View-Only Access
                  </Typography>
                  <Typography variant="body2" className={classes.oldEhrDescription}>
                    Access your existing data in read-only mode. You can view information
                    but won&rsquo;t be able to make changes in the old system.
                  </Typography>
                </Box>
              </CardContent>
              <CardActions className={classes.cardActions}>
                <EHRButton
                  variant="outlined"
                  color="primary"
                  text={isLoading === 'old' ? 'Loading...' : 'View Old System'}
                  disabled={!!isLoading}
                  fullWidth
                />
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        <Alert severity="info" style={{ marginTop: '2rem', textAlign: 'center' }}>
          <Typography variant="body2">
            <strong>Recommendation:</strong> We recommend using the new system for the best experience. 
            The old system is provided for reference only.
          </Typography>
        </Alert>
      </Box>
    </Container>
  );
};

export default MigrationChoicePage;